import { Component, computed, output, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { EmailMessageRequest, EmailService } from '@maximizer/ai/data-access';
import { GlobalStore } from '@maximizer/core/shared/data-access';
import { v4 as uuidV4 } from 'uuid';

const CASE_NUMBER_LENGTH = 8;
import { catchError, finalize, of, tap } from 'rxjs';
import { TrackerService } from '@maximizer/outlook/shared/data-access';
import {
  EventAction,
  EventJourney,
  EventModule,
} from '@maximizer/outlook/shared/domain';

@Component({
  selector: 'maximizer-outlook-suggest-prompt',
  template: `
    <kendo-dialog
      #suggestionContainer
      (close)="onClose()"
      width="95%"
      maxWidth="500px"
    >
      <div class="flex flex-col h-full">
        <div
          class="reset px-2 py-1 flex items-center justify-between bg-neutral-10"
        >
          <p class="subtitle-1 text-black">
            {{ 'outlook.iqBoost.suggestPrompt.title' | translate }}
          </p>
          <button
            kendoButton
            themeColor="tertiary"
            iconClass="fa-icons icon-xmark icon-14"
            [attr.aria-label]="'outlook.buttons.close' | translate"
            class="small"
            (click)="onClose()"
            [disabled]="sendingSuggestion()"
          ></button>
        </div>
        <div class="flex-1 p-3 flex flex-col gap-3 overflow-y-auto">
          <p class="text-body text-neutral-70">
            {{ 'outlook.iqBoost.suggestPrompt.body' | translate }}
          </p>

          <div class="flex flex-col gap-1">
            <span class="text-body bold text-neutral-90">
              {{ 'outlook.iqBoost.suggestPrompt.suggestion' | translate }}
            </span>
            <kendo-textarea
              [placeholder]="
                'outlook.iqBoost.suggestPrompt.placeholder' | translate
              "
              [value]="body()"
              (valueChange)="onBodyChange($event)"
              [rows]="6"
              class="text-body h-"
              resizable="none"
              [disabled]="sendingSuggestion()"
            ></kendo-textarea>
            @if (bodyDirty() && isBodyInvalid()) {
              <p class="text-caption text-error mt-1">
                {{ 'outlook.iqBoost.suggestPrompt.invalidBody' | translate }}
              </p>
            }
          </div>
        </div>

        <div class="flex-none p-3 flex justify-end gap-2">
          <button
            kendoButton
            themeColor="secondary"
            class="uppercase"
            (click)="onClose()"
          >
            {{ 'outlook.iqBoost.suggestPrompt.buttons.cancel' | translate }}
          </button>
          <button
            kendoButton
            themeColor="primary"
            class="uppercase"
            [disabled]="isBodyInvalid()"
            (click)="onSend()"
          >
            {{ 'outlook.iqBoost.suggestPrompt.buttons.send' | translate }}
          </button>
        </div>
      </div>
    </kendo-dialog>
  `,
  imports: [KENDO_DIALOG, KENDO_BUTTON, KENDO_INPUTS, TranslatePipe],
})
export class OutlookSuggestPromptComponent {
  closeDialog = output<void>();
  notifyResult = output<{ type: 'success' | 'error'; messageKey: string }>();

  readonly body = signal('');
  readonly bodyDirty = signal(false);
  readonly sendingSuggestion = signal<boolean>(false);

  isBodyInvalid = computed(() => {
    const text = this.body().trim();
    return !text || !/[a-zA-Z]/.test(text);
  });

  constructor(
    private readonly emailService: EmailService,
    private readonly globalStore: GlobalStore,
    private readonly translate: TranslateService,
    private readonly tracker: TrackerService,
  ) {}

  onBodyChange(value: string): void {
    this.body.set(value);
    if (!this.bodyDirty()) this.bodyDirty.set(true);
  }

  onClose(): void {
    if (this.sendingSuggestion()) return;
    this.closeDialog.emit();
  }

  onSend(): void {
    if (this.isBodyInvalid()) return;

    this.tracker.trackEvent({
      module: EventModule.IqBoost,
      journey: EventJourney.UserSuggestion,
      action: EventAction.Submitted,
    });

    const caseNumber = uuidV4().substring(0, CASE_NUMBER_LENGTH);
    const to = 'pm@maximizer.com';
    const user = this.globalStore.session()?.user;
    const userEmail =
      user?.email1?.email ?? user?.email2?.email ?? user?.email3?.email ?? '';

    const subject = this.translate
      .instant('outlook.iqBoost.suggestPrompt.email.subject')
      .replace('{{firstName}}', user?.firstName ?? '')
      .replace('{{lastName}}', user?.lastName ?? '')
      .replace('{{caseNumber}}', caseNumber);

    const emailBody = this.translate
      .instant('outlook.iqBoost.suggestPrompt.email.body')
      .replace('{{firstName}}', user?.firstName ?? '')
      .replace('{{lastName}}', user?.lastName ?? '')
      .replace('{{email}}', userEmail)
      .replace('{{databaseId}}', this.globalStore.session()?.database ?? '')
      .replace('{{caseNumber}}', caseNumber)
      .replace('{{suggestion}}', this.body());

    const request: EmailMessageRequest = {
      EmailMessage: {
        Data: {
          From: userEmail,
          To: [to],
          Subject: subject,
          Type: 0,
          Body: emailBody,
        },
      },
    };

    this.sendingSuggestion.set(true);
    this.emailService
      .sendSuggestion(request)
      .pipe(
        tap((response) => {
          const messageKey = response
            ? 'outlook.iqBoost.suggestPrompt.notifications.success'
            : 'outlook.iqBoost.suggestPrompt.notifications.error';
          this.notifyResult.emit({
            type: response ? 'success' : 'error',
            messageKey,
          });
          this.closeDialog.emit();
        }),
        catchError(() => {
          this.notifyResult.emit({
            type: 'error',
            messageKey: 'outlook.iqBoost.suggestPrompt.notifications.error',
          });
          this.closeDialog.emit();
          return of(null);
        }),
        finalize(() => {
          this.sendingSuggestion.set(false);
        }),
      )
      .subscribe();
  }
}
